import { usePageContent } from "@robe/client/xcore-ui";
import { Box, BoxProps, Collapse, Flex, Icon } from "@xcorejs/ui";
import DropIcon8 from "components/Icons/8/drop.svg";
import Accordion from "design-system/xcore/Accordion";
import AccordionHeader from "design-system/xcore/Accordion/AccordionHeader";
import AccordionItem from "design-system/xcore/Accordion/AccordionItem";
import AccordionPanel from "design-system/xcore/Accordion/AccordionPanel";
import { useAccordion } from "design-system/xcore/Accordion/data";
import { Fragment, useState, SetStateAction, Dispatch } from "react";
import { useLayout } from "xcore";
import { ProductOverviewPageValues } from "xcore/types";
import { ActiveNavLinkStyle, NavItemProps, SubNavLinkStyle } from ".";
import NavItemLink from "./NavItemLink";
import { HeaderState } from "../index";

type NavItemAccordionProps = Omit<BoxProps, "content"> &
  NavItemProps & {
    dropdownItems?: [NavItemProps, NavItemProps[] | undefined][];
    overviewLink?: NavItemProps;
    activeOn?: string[];
    setState?: Dispatch<SetStateAction<HeaderState>>;
  };

const NavItemAccordion = ({ dropdownItems, overviewLink, activeOn, setState, ...props }: NavItemAccordionProps) => {
  const content = usePageContent();
  const { accordion } = useAccordion({ multiple: true });
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  return (
    <Flex
      display={{ _: "flex", md: "none" }}
      flexDirection="column"
      flex="0 0 auto"
      justifyContent="flex-start"
      width={{ _: "100vw", md: "auto" }}
    >
      <Flex position="relative" justifyContent="center">
        <NavItemLink
          flexDirection="row"
          color={showMobileMenu ? "black" : "#939598"}
          {...(!!dropdownItems?.length && {
            onClick: (e) => {
              e.preventDefault();
              setShowMobileMenu(!showMobileMenu);
            },
          })}
          {...(activeOn?.includes(content.type) && ActiveNavLinkStyle)}
          display={{ _: "flex", md: "none" }}
          {...props}
          variant="accordion"
        />
        {!!dropdownItems?.length && (
          <Box
            position="absolute"
            right="2rem"
            top={0}
            width="2.2rem"
            height="2.2rem"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            cursor="pointer"
          >
            <Icon
              svg={<DropIcon8 />}
              fill={showMobileMenu ? "black" : "#939598"}
              transition="transform 300ms"
              position="absolute"
              top="50%"
              left="50%"
              transform={showMobileMenu ? "translate(-50%, -50%) rotate(-180deg)" : "translate(-50%, -50%)"}
            />
          </Box>
        )}
      </Flex>

      <Collapse open={showMobileMenu} borderTop="2px solid #dee1e5" borderBottom="2px solid #dee1e5" py="0.5rem" mt="1rem">
        <Accordion {...accordion} width={{ _: "100vw", md: "auto" }}>
          {overviewLink && (
            <AccordionItem>
              <NavItemLink
                {...overviewLink}
                {...SubNavLinkStyle}
                pl="2rem"
                pr="2rem"
                {...overviewLinkStyle}
                variant="accordion"
                justifyContent="flex-start"
              />
            </AccordionItem>
          )}
          {dropdownItems?.map(([item, subItems], i) => (
            <AccordionItem key={i}>
              {subItems && subItems.length > 0 ? (
                <>
                  <AccordionHeader
                    icon={<DropIcon8 />}
                    _icon={{ width: "2.2rem", height: "2.2rem", display: "flex", alignItems: "center", justifyContent: "center" }}
                    _expanded={{ _icon: { fill: "black" } }}
                    {...AccordionLinkStyle}
                  >
                    <NavItemLink
                      {...item}
                      variant="accordion"
                      onClick={(e) => {
                        e.preventDefault();
                        setState?.(null); // close menu on item click
                      }}
                      {...SubNavLinkStyle}
                      justifyContent="flex-start"
                    />
                  </AccordionHeader>
                  <AccordionPanel {...AccordionPanelStyle}>{subItems?.map((s, ii) => <SubItem key={ii} {...s} />)}</AccordionPanel>
                </>
              ) : (
                <NavItemLink
                  {...item}
                  {...SubNavLinkStyle}
                  onClick={() => setState?.(null)} // close menu on item click
                  pl="2rem"
                  pr="2rem"
                  variant="accordion"
                  justifyContent="flex-start"
                />
              )}
            </AccordionItem>
          ))}
        </Accordion>
      </Collapse>
    </Flex>
  );
};

export default NavItemAccordion;

const SubItem = (props: NavItemProps) => {
  const { stringify, localize } = useLayout();

  if (props.content?.type === "productOverviewPage" && props.content.values) {
    const items = localize((props.content.values as Pick<ProductOverviewPageValues, "itemsRelation">)?.itemsRelation);

    if (items) {
      return (
        <AccordionItem index={props.content!.id}>
          <AccordionHeader
            icon={<DropIcon8 />}
            _icon={{ width: "2.2rem", height: "2.2rem", display: "flex", alignItems: "center", justifyContent: "center" }}
            _expanded={{ _icon: { fill: "black" } }}
            {...AccordionLinkStyle}
            bg="transparent"
            px="0"
          >
            <NavItemLink
              {...props}
              variant="accordion"
              onClick={(e) => e.preventDefault()}
              {...SubNavLinkStyle}
              justifyContent="flex-start"
            />
          </AccordionHeader>
          <AccordionPanel {...AccordionPanelStyle}>
            {items?.map((item, ii) => {
              if (!item?.type || !item?.slug) {
                return <Fragment key={ii} />;
              }

              return (
                <SubItem key={ii} content={{ id: item.id, slug: item.slug, type: item.type, values: item?.values }}>
                  {stringify(item.title)}
                </SubItem>
              );
            })}
          </AccordionPanel>
        </AccordionItem>
      );
    }
  }

  return <NavItemLink {...props} {...SubNavLinkStyle} variant="accordion" justifyContent="flex-start" />;
};

const overviewLinkStyle = {
  textTransform: "uppercase",
};

const AccordionLinkStyle = {
  alignItems: "center",
  justifyContent: "space-between",
  px: "2rem",
  py: 0,
};

const AccordionPanelStyle = {
  bg: "#dee1e5",
  pb: "0",
  px: "2rem",
  py: "0.5rem",
};
